/**
 * Builds the shared SSE `ChatTransport` (the same class the ai-teammate drawer streams through) with the
 * account/user identity plus the terms-of-service ref-mirror. Extracted from the copy in the drawer's
 * `send-message-streaming.js`; consumed by the chatbox surface only for now — the drawer migrates onto
 * this shared hook in a follow-up (kept out of this PR to bound the sanctioned ai-teammate diff).
 */

import { useEffect, useMemo, useRef } from "react"
import { useSelector } from "react-redux"
import { getNeedSignTerms } from "modules/account-root/selectors"
import { useAccountAlias, useUserId } from "resources/account"
import { ChatTransport } from "connected-components/ai-teammate/utils/chat-transport"
import type { StreamingChatTransport } from "./streaming-transport"

export function useChatTransport(): StreamingChatTransport {
  const accountAlias = useAccountAlias()
  const userId = useUserId()

  // Event-time mirror for the transport's terms gate (read from the fetch, not render).
  const needSignTerms = useSelector(getNeedSignTerms)
  const needSignTermsRef = useRef(needSignTerms)
  useEffect(() => {
    needSignTermsRef.current = needSignTerms
  }, [needSignTerms])

  // URLs scope to the alias; token to account+user. Rebuild only when identity changes.
  return useMemo(
    () => new ChatTransport({ accountAlias, account: accountAlias, userId, getNeedSignTerms: () => needSignTermsRef.current }),
    [accountAlias, userId],
  )
}
